import { Box, Stack, Tab, Tabs } from '@mui/material'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { downloadAdminCertificate } from 'api/certificates'
import { getCohorts } from 'api/cohorts'
import { getCertificatesPerUserReport, getTotalEngagementPerUserReport } from 'api/reports'
import columnRenderers, { PLACEHOLDER } from 'components/column-renderers'
import { formatOrgStateUnit } from 'components/column-renderers/orgStateUnit'
import ConfirmModal from 'components/modals/ConfirmModal'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import TableToolbar from 'components/table/TableToolbar'
import { getTitle } from 'components/tabs/TabsTitle'
import { useGenerateRecords } from 'features/Reports/hooks/useGenerateRecords'
import {
  getActionColumn,
  RowAction,
  UIFilterAsyncAutocomplete,
  useOrgStateUnitFilter,
} from 'features/UI'
import { CSVField, useExportCSV } from 'hooks/useExportCSV'
import { useGridControl } from 'hooks/useGridControl'
import { useRefreshTable } from 'hooks/useRefreshTable'
import { useShowControl } from 'hooks/useShowControl'
import capitalize from 'lodash/capitalize'
import moment from 'moment/moment'
import React, { useCallback, useMemo, useState } from 'react'
import { useMutation } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { routes } from 'routes/routes'
import {
  BaseTableRequest,
  CertificateRuleTypeLabel,
  CertificateType,
  EngagementPerUserCertificate,
  EngagementReportTypes,
  FilterModel,
  FilterModelNumberArray,
  Option,
  OrgReportTypes,
} from 'types'
import { downloadArrayBuffer, formatDate, getRoute, useGetDataSource } from 'utils'
import { useUserStatusFilter } from '../../../User/hooks/useUserStatusFilter'
import { createEngagementColumn, createEngagementCsvColumn } from '../../helpers/engagementColumn'
import { prepareDateRangeParams } from '../../helpers/prepareDateRangeParams'
import { useDateRange } from '../../hooks/useDateRange'
import { DateRangeToolbar } from '../DateRangeToolbar/DateRangeToolbar'

export const certificatesOrgColumnDefs: ColDef[] = [
  { headerName: 'First Name', field: 'firstname', filter: 'agTextColumnFilter' },
  { headerName: 'Last Name', field: 'lastname', filter: 'agTextColumnFilter' },
  {
    headerName: 'ORG/State/UNIT',
    field: 'organizationName',
    minWidth: 300,
    cellRenderer: columnRenderers.accessCodeRenderer,
  },
  {
    headerName: 'User Status',
    field: 'userStatus',
    cellRenderer: columnRenderers.capitalizeRenderer('userStatus'),
  },
  { headerName: 'Certificate Name', field: 'name', filter: 'agTextColumnFilter', minWidth: 300 },
  {
    headerName: 'Date',
    field: 'createdAt',
    cellRenderer: columnRenderers.date('createdAt'),
  },
  {
    headerName: 'Certificate Type',
    field: 'type',
    valueGetter: ({ data }: { data: EngagementPerUserCertificate }) => {
      if (!data) return PLACEHOLDER
      return CertificateRuleTypeLabel[data.type] || PLACEHOLDER
    },
  },
  { headerName: 'Time Spend (h)', field: 'timeSpent' },
]

export const csvFieldsCertificates: CSVField[] = [
  { label: 'First Name', value: 'firstname' },
  { label: 'Last Name', value: 'lastname' },
  { label: 'ORG/State/UNIT', value: formatOrgStateUnit },
  {
    label: 'User Status',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: ({ userStatus }: any) => {
      return userStatus ? capitalize(userStatus) : PLACEHOLDER
    },
  },
  { label: 'Certificate Name', value: 'name' },
  {
    label: 'Date',
    value: (data: EngagementPerUserCertificate) =>
      formatDate(data.createdAt, 'YYYY-MM-DD HH:mm:ss'),
  },
  {
    label: 'Certificate Type',
    value: (data: EngagementPerUserCertificate) => {
      return CertificateRuleTypeLabel[data.type] || PLACEHOLDER
    },
  },
  { label: 'Time Spend (h)', value: 'timeSpent' },
]

const activeColumnDefs: ColDef[] = [
  { headerName: 'First Name', field: 'userFirstName', filter: 'agTextColumnFilter' },
  { headerName: 'Last Name', field: 'userLastName', filter: 'agTextColumnFilter' },
  {
    headerName: 'ORG/State/UNIT',
    field: 'organizationName',
    minWidth: 300,
    cellRenderer: columnRenderers.accessCodeRenderer,
  },
  {
    headerName: 'User Status',
    field: 'userStatus',
    cellRenderer: columnRenderers.capitalizeRenderer('userStatus'),
  },
  createEngagementColumn('Total Time Online', 'totalEngagement'),
  { headerName: 'Finished Lessons', field: 'passedLessonsCount', filter: 'agNumberColumnFilter' },
  { headerName: 'Passed Courses', field: 'passedCoursesCount', filter: 'agNumberColumnFilter' },
]

const csvFieldsActive: CSVField[] = [
  { label: 'First Name', value: 'userFirstName' },
  { label: 'Last Name', value: 'userLastName' },
  { label: 'ORG/State/UNIT', value: formatOrgStateUnit },
  {
    label: 'User Status',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: ({ userStatus }: any) => {
      return userStatus ? capitalize(userStatus) : PLACEHOLDER
    },
  },
  createEngagementCsvColumn('Total Time Online', 'totalEngagement'),
  { label: 'Finished Lessons', value: 'passedLessonsCount' },
  { label: 'Passed Courses', value: 'passedCoursesCount' },
]

type IReportsTabOptions = {
  [key in OrgReportTypes]: {
    columns: ColDef[]
    title: string
    csvName: string
    csvFields: CSVField[]
    hasDateFilter: boolean
    hasCohortsFilter: boolean
    hasGenerateReport: boolean
  }
}

const ReportsTabOptions: IReportsTabOptions = {
  [OrgReportTypes.Completion]: {
    title: 'Training Completion per User',
    columns: activeColumnDefs,
    csvName: 'Training Completion per User.csv',
    csvFields: csvFieldsActive,
    hasDateFilter: true,
    hasCohortsFilter: false,
    hasGenerateReport: true,
  },
  [EngagementReportTypes.Certificates]: {
    title: 'Certificates per User',
    columns: certificatesOrgColumnDefs,
    csvName: 'Certificates per User.csv',
    csvFields: csvFieldsCertificates,
    hasDateFilter: true,
    hasCohortsFilter: false,
    hasGenerateReport: false,
  },
}

const ReportOrder = [OrgReportTypes.Completion, OrgReportTypes.Certificates]

export const ReportOrganizationCompletion = () => {
  const navigate = useNavigate()
  const { type } = useParams()

  const [selectedCertificate, setSelectedCertificate] = useState<{
    id: number
    type: CertificateType
  } | null>(null)

  const [isOpenConfirmModal, onOpenConfirmModal, onCloseConfirmModal] = useShowControl()

  const selectedTab = useMemo(() => {
    if (!type) return OrgReportTypes.Completion

    return type as OrgReportTypes
  }, [type])

  const tabOptions = useMemo(() => ReportsTabOptions[selectedTab], [selectedTab])

  const handleChangeSelectedTab = useCallback(
    (_: React.SyntheticEvent, newValue: OrgReportTypes) => {
      const route = getRoute(routes.orgReports, { type: newValue })
      navigate(route)
    },
    [navigate],
  )

  const downloadMutation = useMutation(downloadAdminCertificate)

  const handleDownloadCertificate = useCallback(
    (id: number, row: ICellRendererParams<{ type: CertificateType }>) => {
      if (!row.data) return
      const { type } = row.data

      setSelectedCertificate({ id, type })
      onOpenConfirmModal()
    },
    [onOpenConfirmModal],
  )

  const handleConfirmDownload = useCallback(async () => {
    if (!selectedCertificate) return

    try {
      const response = await downloadMutation.mutateAsync({
        id: selectedCertificate.id,
        type: selectedCertificate.type,
        timezone: moment().format('Z'),
      })
      downloadArrayBuffer(response)
      onCloseConfirmModal()
    } catch (e) {
      console.error(e)
    }
  }, [selectedCertificate, downloadMutation, onCloseConfirmModal])

  const certificateActions: RowAction[] = useMemo(() => {
    return [{ name: 'Download Certificate', onClick: handleDownloadCertificate }]
  }, [handleDownloadCertificate])

  const columns = useMemo(() => {
    let cols = tabOptions.columns

    if (selectedTab === OrgReportTypes.Certificates) {
      cols = cols.concat({ ...getActionColumn(certificateActions) })
    }

    return cols
  }, [tabOptions.columns, selectedTab, certificateActions])

  const {
    rangeType,
    year,
    month,
    selectedDate,
    dateInterval,
    onChangeDateInterval,
    onChangeRange,
    onChangeYear,
    onChangeMonth,
    setSelectedDate,
  } = useDateRange()

  const { GenerateRecordsButton, setTableRequest } = useGenerateRecords({
    rangeType,
    year,
    month,
    selectedDate,
    dateInterval,
  })

  const { UserStatusFilter, userStatusFilterValue } = useUserStatusFilter()
  const { OrgStateUnitFilter, orgStateUnitFilterValue } = useOrgStateUnitFilter()

  const [cohortsFilterValue, setCohortsFilterValue] = useState<Option[]>([])

  const getDataWrapper = useCallback(
    (req: BaseTableRequest) => {
      setTableRequest(req)
      const dateParams = prepareDateRangeParams(rangeType, year, month, selectedDate, dateInterval)

      const filter: FilterModel = {
        ...(req.filter || {}),
      }

      if (userStatusFilterValue) {
        filter.userStatus = userStatusFilterValue
      }

      if (orgStateUnitFilterValue) {
        filter.accessCodeId = orgStateUnitFilterValue
      }

      if (selectedTab === OrgReportTypes.Completion) {
        return getTotalEngagementPerUserReport({ ...req, filter, params: dateParams })
      }

      // Certificates report
      if (cohortsFilterValue.length > 0) {
        filter.cohortId = {
          type: 'number',
          op: 'in',
          value: cohortsFilterValue.map((cohort) => cohort.value),
        } as FilterModelNumberArray
      }

      const request = { ...req, filter, params: dateParams }
      return getCertificatesPerUserReport(request)
    },
    [
      dateInterval,
      month,
      rangeType,
      selectedDate,
      selectedTab,
      setTableRequest,
      year,
      cohortsFilterValue,
      userStatusFilterValue,
      orgStateUnitFilterValue,
    ],
  )

  const getDataSourceActive = useGetDataSource(getDataWrapper)
  const { onGridReady, gridApi, columnApi } = useGridControl(getDataSourceActive)

  const { csvLoading, onExportCSV } = useExportCSV(
    getDataWrapper,
    gridApi,
    columnApi,
    tabOptions.csvFields,
    tabOptions.csvName,
  )

  useRefreshTable({ params: { rangeType, year, month, selectedDate }, gridApi })

  const handleChangeCohortFilter = useCallback(
    (newValue: Option[]) => {
      setCohortsFilterValue(newValue)
      // Infinite row model refresh
      gridApi?.purgeInfiniteCache?.()
    },
    [gridApi],
  )

  return (
    <>
      <TableToolbar
        left={
          <Tabs
            value={type}
            onChange={handleChangeSelectedTab}
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons={false}
          >
            {ReportOrder.map((type) => (
              <Tab key={type} value={type} label={getTitle(ReportsTabOptions[type].title)} />
            ))}
          </Tabs>
        }
        exportCSVEnable
        csvLoading={csvLoading}
        onExportCSV={onExportCSV}
      />

      <Stack flexDirection="row" mt={1} mb={2} className="gap-16">
        {tabOptions.hasDateFilter && (
          <DateRangeToolbar
            rangeType={rangeType}
            year={year}
            month={month}
            selectedDate={selectedDate}
            dateInterval={dateInterval}
            onChangeDateInterval={onChangeDateInterval}
            onChangeRange={onChangeRange}
            onChangeYear={onChangeYear}
            onChangeMonth={onChangeMonth}
            setSelectedDate={setSelectedDate}
          />
        )}

        {tabOptions.hasCohortsFilter && (
          <Box minWidth="200px">
            <UIFilterAsyncAutocomplete
              label="User Tags"
              value={cohortsFilterValue}
              onChange={handleChangeCohortFilter}
              queryId="cohorts"
              apiRequest={getCohorts}
            />
          </Box>
        )}

        <Box minWidth="200px">{UserStatusFilter}</Box>

        {OrgStateUnitFilter}

        {tabOptions.hasGenerateReport && (
          <>
            <Box flex={1} />
            {GenerateRecordsButton}
          </>
        )}
      </Stack>

      <InfinityAgGrid columnDefs={columns} pagination onGridReady={onGridReady} />

      {isOpenConfirmModal && (
        <ConfirmModal
          title="Download Certificate"
          body="Are you sure you want to download this certificate?"
          handleClose={onCloseConfirmModal}
          handleConfirm={handleConfirmDownload}
          loading={downloadMutation.isLoading}
        />
      )}
    </>
  )
}
